import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Ship, Plus, RefreshCw, Trash2, Boxes, ClipboardList } from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import { shipmentsService } from '../services/shipments.service';
import {
  PRORATION_METHODS,
  PRORATION_METHOD_LABELS,
  type ProrationMethod,
  type QuoteShipment,
} from '../types/quote-shipment.types';
import type { QuoteSection } from '../types/quote.types';
import type {
  QuoteFreightLeg,
  CreateQuoteFreightLegData,
  SurchargeType,
} from '../types/quote-freight.types';
import { QuoteFreightLegCard } from './quote-freight-leg-card';
import { AddFreightLegDialog } from './add-freight-leg-dialog';
import { LoadFreightRouteDialog } from './load-freight-route-dialog';
import {
  useQuoteFreightCalculation,
  type CalculatedLeg,
} from '../hooks/use-quote-freight-calculation';
import { formatNumber } from '@/lib/utils/format';
import type { FreightRoute } from '@/features/master-data/types/freight-route.types';

interface QuoteLogisticsSectionProps {
  quoteId?: string | null;
  incoterm: string | null;
  incotermConfig?: {
    paysInlandOrigin: boolean;
    paysInternational: boolean;
    paysInlandDest: boolean;
    paysHandling: boolean;
    paysInsurance: boolean;
    paysDuties: boolean;
  } | null;
  /** Líneas de la cotización (para derivar totales globales de peso/volumen/FOB como fallback) */
  sections?: QuoteSection[];
  /** Lista de materiales para mostrar nombres en la asignación */
  materials?: Array<{ id: string; code: string; name: string }>;
  onFreightCalculated?: (totalFreight: number, totalInsurance: number) => void;
  onLegsChanged?: (legs: QuoteFreightLeg[]) => void;
  onShipmentsChanged?: (shipments: QuoteShipment[]) => void;
  onRequireSave?: () => Promise<string | null> | void;
  /** Callback para actualizar shipmentId de una línea en el estado del pricer (para líneas no guardadas) */
  onLineShipmentChanged?: (lineItemId: string, shipmentId: string | null) => void;
  readOnly?: boolean;
}

/**
 * Sección unificada de logística para cotizaciones de importación.
 *
 * UX unificada: SIEMPRE se renderiza como tarjetas por envío (una o más).
 * Cada tarjeta permite gestión completa de tramos: agregar (dialog), editar
 * (inline con surcharges/insurance/etc.), eliminar, y cargar desde ruta.
 */
export function QuoteLogisticsSection({
  quoteId: quoteIdProp,
  incoterm,
  incotermConfig = null,
  sections = [],
  materials = [],
  onFreightCalculated,
  onLegsChanged,
  onShipmentsChanged,
  onRequireSave,
  onLineShipmentChanged,
  readOnly = false,
}: QuoteLogisticsSectionProps) {
  const { toast } = useToast();
  const [localQuoteId, setLocalQuoteId] = useState<string | null>(null);
  const quoteId = quoteIdProp || localQuoteId;
  const [shipments, setShipments] = useState<QuoteShipment[]>([]);
  const [loading, setLoading] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [recalculatingId, setRecalculatingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Diálogos por shipment
  const [addLegDialogShipmentId, setAddLegDialogShipmentId] = useState<string | null>(null);
  const [loadRouteDialogShipmentId, setLoadRouteDialogShipmentId] = useState<string | null>(null);

  const fmt = (v: number) => `$${formatNumber(v ?? 0)}`;

  const load = useCallback(
    async (opts?: { autoCreateDefault?: boolean }) => {
      if (!quoteId) return;
      setLoading(true);
      try {
        let data = await shipmentsService.list(quoteId);
        if ((data?.length ?? 0) === 0 && opts?.autoCreateDefault && !readOnly) {
          try {
            const created = await shipmentsService.create(quoteId, {
              name: 'Envío principal',
              prorationMethod: 'FOB_VALUE',
              incoterm: incoterm ?? undefined,
            });
            data = [created];
          } catch {
            // non-fatal
          }
        }
        setShipments(data);
        onShipmentsChanged?.(data);
      } catch {
        toast({
          title: 'Error',
          description: 'No se pudieron cargar los envíos',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    },
    [quoteId, readOnly, incoterm, toast, onShipmentsChanged]
  );

  useEffect(() => {
    if (quoteId) load({ autoCreateDefault: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId]);

  const [autoSaveFailed, setAutoSaveFailed] = useState(false);
  const handleAutoSave = async () => {
    if (!onRequireSave) return;
    setAutoSaving(true);
    setAutoSaveFailed(false);
    try {
      const newId = await onRequireSave();
      if (newId) {
        setLocalQuoteId(newId);
      } else {
        // El padre no pudo guardar (ej: falta clientName). Permitimos reintentar más tarde.
        setAutoSaveFailed(true);
        autoTriggeredRef.current = false;
      }
    } finally {
      setAutoSaving(false);
    }
  };

  const handleUpdate = async (
    shipmentId: string,
    patch: Partial<{
      name: string;
      origin: string;
      incoterm: string;
      prorationMethod: ProrationMethod;
      notes: string;
      manualWeight: number | null;
      manualVolume: number | null;
      manualFob: number | null;
    }>
  ) => {
    if (!quoteId) return;
    try {
      const updated = await shipmentsService.update(quoteId, shipmentId, patch);
      setShipments((prev) => {
        const next = prev.map((s) => (s.id === shipmentId ? { ...s, ...updated } : s));
        onShipmentsChanged?.(next);
        return next;
      });
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el envío',
        variant: 'destructive',
      });
    }
  };

  const handleCreateExtra = async () => {
    if (!quoteId) return;
    setCreating(true);
    try {
      const nextIndex = shipments.length + 1;
      const created = await shipmentsService.create(quoteId, {
        name: `Envío ${nextIndex}`,
        prorationMethod: 'FOB_VALUE',
      });
      setShipments((prev) => {
        const next = [...prev, created];
        onShipmentsChanged?.(next);
        return next;
      });
      toast({ title: 'Envío creado', description: created.name });
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudo crear el envío',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!quoteId || !deleteId) return;
    try {
      await shipmentsService.remove(quoteId, deleteId);
      setShipments((prev) => {
        const next = prev.filter((s) => s.id !== deleteId);
        onShipmentsChanged?.(next);
        return next;
      });
      toast({ title: 'Envío eliminado' });
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el envío',
        variant: 'destructive',
      });
    } finally {
      setDeleteId(null);
    }
  };

  // ---------- Legs CRUD (por shipment) ----------

  const refreshShipment = async (shipmentId: string) => {
    if (!quoteId) return;
    try {
      const updated = await shipmentsService.recalculate(quoteId, shipmentId);
      setShipments((prev) => {
        const next = prev.map((s) => (s.id === shipmentId ? updated : s));
        onShipmentsChanged?.(next);
        return next;
      });
    } catch {
      // Si el recalc falla, al menos refrescamos la lista para tener los legs actualizados.
      await load();
    }
  };

  const handleAddLegToShipment = async (shipmentId: string, data: CreateQuoteFreightLegData) => {
    if (!quoteId) return;
    try {
      await shipmentsService.createLeg(quoteId, shipmentId, data);
      toast({
        title: 'Tramo agregado',
        description: `${data.origin} → ${data.destination}`,
      });
      await refreshShipment(shipmentId);
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudo agregar el tramo',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateLeg = async (
    shipmentId: string,
    legId: string,
    data: Partial<QuoteFreightLeg>
  ) => {
    if (!quoteId) return;
    // Optimistic update
    setShipments((prev) => {
      const next = prev.map((s) => {
        if (s.id !== shipmentId) return s;
        return {
          ...s,
          legs: s.legs.map((l) => (l.id === legId ? { ...l, ...data } : l)),
        };
      });
      return next;
    });
    try {
      await shipmentsService.updateLeg(quoteId, shipmentId, legId, data);
      await refreshShipment(shipmentId);
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el tramo',
        variant: 'destructive',
      });
      await load();
    }
  };

  const handleDeleteLeg = async (shipmentId: string, legId: string) => {
    if (!quoteId) return;
    try {
      await shipmentsService.deleteLeg(quoteId, shipmentId, legId);
      toast({ title: 'Tramo eliminado' });
      await refreshShipment(shipmentId);
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el tramo',
        variant: 'destructive',
      });
    }
  };

  const handleAddSurcharge = async (
    shipmentId: string,
    legId: string,
    data: { concept: string; type: SurchargeType; value: number }
  ) => {
    if (!quoteId) return;
    try {
      await shipmentsService.addSurchargeToLeg(quoteId, shipmentId, legId, data);
      await refreshShipment(shipmentId);
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudo agregar el recargo',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteSurcharge = async (shipmentId: string, legId: string, surchargeId: string) => {
    if (!quoteId) return;
    try {
      await shipmentsService.deleteSurchargeFromLeg(quoteId, shipmentId, legId, surchargeId);
      await refreshShipment(shipmentId);
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el recargo',
        variant: 'destructive',
      });
    }
  };

  const handleLoadFromRoute = async (shipmentId: string, route: FreightRoute) => {
    if (!quoteId) return;
    try {
      await shipmentsService.loadFromRoute(quoteId, shipmentId, route.id);
      toast({
        title: 'Ruta cargada',
        description: `Se cargaron los tramos desde "${route.name}".`,
      });
      await refreshShipment(shipmentId);
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudo cargar la ruta',
        variant: 'destructive',
      });
    }
  };

  const handleRecalculate = async (shipmentId: string) => {
    if (!quoteId) return;
    setRecalculatingId(shipmentId);
    try {
      const updated = await shipmentsService.recalculate(quoteId, shipmentId);
      setShipments((prev) => {
        const next = prev.map((s) => (s.id === shipmentId ? updated : s));
        onShipmentsChanged?.(next);
        return next;
      });
      toast({ title: 'Envío recalculado', description: updated.name });
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudo recalcular',
        variant: 'destructive',
      });
    } finally {
      setRecalculatingId(null);
    }
  };

  // ---------- Asignación de líneas a envíos ----------

  const allLineItems = useMemo(() => {
    const items: Array<{
      id: string;
      materialId: string;
      materialLabel: string;
      quantity: number;
      fob: number;
      shipmentId?: string | null;
    }> = [];
    for (const section of sections) {
      for (const li of (section.lineItems ?? []) as any[]) {
        const mat = materials.find((m) => m.id === li.materialId);
        items.push({
          id: li.id,
          materialId: li.materialId,
          materialLabel: mat ? `${mat.code} — ${mat.name}` : li.materialId?.slice(0, 8) || '—',
          quantity: li.quantity || 1,
          fob: Number(li.totalPrice) || 0,
          shipmentId: li.shipmentId ?? null,
        });
      }
    }
    return items;
  }, [sections, materials]);

  const isTempId = (id: string) =>
    id.startsWith('temp-') || id.startsWith('import-') || id.startsWith('loaded-');

  const handleAssignLine = async (shipmentId: string, lineItemId: string) => {
    // Actualizar estado local del pricer
    onLineShipmentChanged?.(lineItemId, shipmentId);

    // Calcular FOB de las líneas que quedarán asignadas
    const updatedItems = allLineItems.map((li) =>
      li.id === lineItemId ? { ...li, shipmentId } : li
    );
    const assignedFob = updatedItems
      .filter((li) => li.shipmentId === shipmentId)
      .reduce((sum, li) => sum + li.fob, 0);

    if (!quoteId) return;
    try {
      // Para líneas persistidas, también asignar en backend
      if (!isTempId(lineItemId)) {
        await shipmentsService.assignItems(quoteId, shipmentId, [lineItemId]);
      }
      // Siempre actualizar FOB (dispara recalculate en backend → totalCost se actualiza)
      if (assignedFob > 0) {
        await shipmentsService.update(quoteId, shipmentId, { manualFob: assignedFob });
      }
      // Refrescar sin remount — solo actualizar shipments data
      const data = await shipmentsService.list(quoteId);
      setShipments(data);
      onShipmentsChanged?.(data);
    } catch {
      /* silent */
    }
  };

  const handleUnassignLine = async (shipmentId: string, lineItemId: string) => {
    onLineShipmentChanged?.(lineItemId, null);

    const updatedItems = allLineItems.map((li) =>
      li.id === lineItemId ? { ...li, shipmentId: null } : li
    );
    const assignedFob = updatedItems
      .filter((li) => li.shipmentId === shipmentId)
      .reduce((sum, li) => sum + li.fob, 0);

    if (!quoteId) return;
    try {
      if (!isTempId(lineItemId)) {
        await shipmentsService.unassignItems(quoteId, shipmentId, [lineItemId]);
      }
      await shipmentsService.update(quoteId, shipmentId, {
        manualFob: assignedFob > 0 ? assignedFob : null,
      });
      const data = await shipmentsService.list(quoteId);
      setShipments(data);
      onShipmentsChanged?.(data);
    } catch {
      /* silent */
    }
  };

  // ---------- Totales globales derivados (fallback por envío) ----------

  /**
   * Totales globales a partir de las líneas del pricer.
   * Se usan como fallback cuando un shipment no tiene line items asignados aún.
   */
  const globalDerivedTotals = useMemo(() => {
    let totalWeight = 0;
    let totalVolume = 0;
    let totalFob = 0;
    for (const section of sections) {
      for (const li of section.lineItems ?? []) {
        const qty = (li as any).quantity || 1;
        const ci = ((li as any).costInputs as Record<string, any>) || {};
        const mat: any = (li as any).material || null;

        const inputWeight = ci.PESO_KG || ci.pesoKg || ci.weight || 0;
        const weightPerUnit = inputWeight > 0 ? Number(inputWeight) : Number(mat?.weight ?? 0);
        totalWeight += weightPerUnit * qty;

        const inputVolume = ci.VOLUMEN_CBM || ci.VOLUMEN_M3 || ci.volumenCbm || ci.volume || 0;
        let volumePerUnit = Number(inputVolume) || 0;
        if (volumePerUnit <= 0 && mat) {
          const w = Number(mat.width ?? 0);
          const h = Number(mat.height ?? 0);
          const d = Number(mat.depth ?? 0);
          volumePerUnit = (w * h * d) / 1_000_000;
        }
        totalVolume += volumePerUnit * qty;

        totalFob += Number((li as any).totalPrice) || 0;
      }
    }
    return { totalWeight, totalVolume, totalFob };
  }, [sections]);

  /**
   * Calcula totales de peso/volumen/FOB para un shipment en base a sus lineItems.
   * Si el shipment no tiene items asignados, cae a los totales globales.
   */
  const computeShipmentTotals = useCallback(
    (shipment: QuoteShipment) => {
      // Usar líneas asignadas localmente (allLineItems, derivadas de sections del pricer)
      // en vez de shipment.lineItems (que viene del API y no tiene líneas temp).
      const localAssigned = allLineItems.filter((li) => li.shipmentId === shipment.id);
      let totalWeight = 0;
      let totalVolume = 0;
      let totalFob = 0;

      if (localAssigned.length === 0) {
        // Fallback: si no hay líneas asignadas localmente, intentar con las del API
        const apiItems = shipment.lineItems ?? [];
        if (apiItems.length === 0) {
          ({ totalWeight, totalVolume, totalFob } = globalDerivedTotals);
        } else {
          for (const li of apiItems as any[]) {
            const qty = li.quantity || 1;
            const ci = (li.costInputs as Record<string, any>) || {};
            const mat = li.material || null;
            const inputWeight = ci.PESO_KG || ci.pesoKg || ci.weight || 0;
            const weightPerUnit = inputWeight > 0 ? Number(inputWeight) : Number(mat?.weight ?? 0);
            totalWeight += weightPerUnit * qty;
            const inputVolume = ci.VOLUMEN_CBM || ci.VOLUMEN_M3 || ci.volumenCbm || ci.volume || 0;
            let volumePerUnit = Number(inputVolume) || 0;
            if (volumePerUnit <= 0 && mat) {
              volumePerUnit =
                (Number(mat.width ?? 0) * Number(mat.height ?? 0) * Number(mat.depth ?? 0)) /
                1_000_000;
            }
            totalVolume += volumePerUnit * qty;
            totalFob += Number(li.totalPrice) || 0;
          }
        }
      } else {
        // Usar datos locales del pricer
        for (const li of localAssigned) {
          totalFob += li.fob;
        }
        // Peso/volumen: derivar de sections (allLineItems no tiene esos campos detallados,
        // pero el backend los recalculará al guardar)
      }

      // Overrides manuales del shipment (mismo comportamiento que backend).
      const mw = (shipment as any).manualWeight;
      const mv = (shipment as any).manualVolume;
      const mf = (shipment as any).manualFob;
      if (mw != null && Number(mw) > 0) totalWeight = Number(mw);
      if (mv != null && Number(mv) > 0) totalVolume = Number(mv);
      if (mf != null && Number(mf) > 0) totalFob = Number(mf);

      return { totalWeight, totalVolume, totalFob };
    },
    [globalDerivedTotals, allLineItems]
  );

  // ---------- Notificar legs combinados al padre (para flujos legacy) ----------
  useEffect(() => {
    if (!onLegsChanged) return;
    const all: QuoteFreightLeg[] = [];
    for (const s of shipments) {
      for (const leg of s.legs ?? []) all.push(leg);
    }
    onLegsChanged(all);
  }, [shipments, onLegsChanged]);

  // ---------- Total global de flete (suma de shipments) ----------
  // Nota: el total real (incluyendo seguro) lo calcula el backend; acá reportamos
  // el agregado de totalCost para mantener sincronizados los parents que ya usaban
  // onFreightCalculated.
  const totalFreightAll = useMemo(
    () => shipments.reduce((acc, s) => acc + Number(s.totalCost ?? 0), 0),
    [shipments]
  );
  useEffect(() => {
    onFreightCalculated?.(totalFreightAll, 0);
  }, [totalFreightAll, onFreightCalculated]);

  // ---------- Save explícito (sin auto-trigger) ----------
  // ANTES: hacíamos handleAutoSave en mount → creaba un draft fantasma en DB
  // sin que el usuario hiciera click. Ahora el save es explícito vía botón
  // "Configurar envío".
  const autoTriggeredRef = useRef(false);

  // ---------- UI: sin quoteId ----------
  if (!quoteId) {
    if (!readOnly && onRequireSave) {
      // Autosave falló (ej: falta clientName). Mostramos un hint claro y un botón de reintento.
      if (autoSaveFailed) {
        return (
          <div className="rounded-lg border border-alerta/30 bg-alerta/5 p-4 text-center text-sm text-foreground">
            <Ship className="mx-auto mb-2 h-6 w-6 text-alerta" />
            <div className="mb-2">
              Completá el nombre del cliente arriba para habilitar la configuración de envío.
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={autoSaving}
              onClick={handleAutoSave}
            >
              {autoSaving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : null}
              Reintentar
            </Button>
          </div>
        );
      }
      // Save en curso: loader.
      if (autoSaving) {
        return (
          <div className="rounded-lg border border-info/20 bg-info/5 p-4 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
            <RefreshCw className="h-3.5 w-3.5 animate-spin text-info" />
            Preparando envío…
          </div>
        );
      }
      // Sin quoteId todavía: mostrar botón explícito (NO auto-crear draft).
      return (
        <div className="rounded-lg border border-dashed border-info/30 bg-info/5 p-4 text-center text-sm text-muted-foreground">
          <Ship className="mx-auto mb-2 h-6 w-6 text-info" />
          <div className="mb-3">Para configurar el envío hay que crear primero el borrador.</div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              autoTriggeredRef.current = true;
              handleAutoSave();
            }}
          >
            <Ship className="mr-2 h-3.5 w-3.5" />
            Configurar envío
          </Button>
        </div>
      );
    }
    // Sin onRequireSave (flujo legacy o readOnly): mensaje informativo.
    return (
      <div className="rounded-lg border border-dashed border-info/30 bg-info/5 p-4 text-center text-sm text-muted-foreground">
        <Ship className="mx-auto mb-2 h-6 w-6 text-info" />
        <div>Para configurar el envío primero hay que guardar la cotización como borrador.</div>
      </div>
    );
  }

  // ---------- UI: cargando ----------
  if (loading && shipments.length === 0) {
    return (
      <div className="rounded-lg border border-info/20 bg-info/5 p-6 text-center text-sm text-muted-foreground">
        Cargando envío...
      </div>
    );
  }

  // ---------- UI: cards (siempre) ----------
  const headerLabel = shipments.length === 1 ? 'Envío' : 'Envíos';
  const activeShipmentForAddLeg = shipments.find((s) => s.id === addLegDialogShipmentId);
  const nextLegOrderForShipment = activeShipmentForAddLeg
    ? (activeShipmentForAddLeg.legs?.length ?? 0) > 0
      ? Math.max(...activeShipmentForAddLeg.legs.map((l) => l.legOrder)) + 1
      : 1
    : 1;

  return (
    <div className="rounded-lg border border-info/20 bg-info/5">
      <div className="flex items-center justify-between border-b border-info/20 px-4 py-3">
        <div className="flex items-center gap-2">
          <Boxes className="h-5 w-5 text-info" />
          <span className="text-sm font-semibold uppercase tracking-wide text-info">
            {headerLabel}
          </span>
          <Badge variant="outline" className="ml-2 text-xs">
            {shipments.length}
          </Badge>
          {incoterm && (
            <Badge variant="outline" className="ml-1 border-info/30 bg-info/20 text-xs text-info">
              {incoterm}
            </Badge>
          )}
        </div>
        {!readOnly && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={handleCreateExtra}
            disabled={creating}
          >
            <Plus className="h-3.5 w-3.5" />
            Nuevo envío
          </Button>
        )}
      </div>

      <div className="space-y-3 p-4">
        {shipments.map((shipment) => (
          <ShipmentCard
            key={shipment.id}
            shipment={shipment}
            incotermConfig={incotermConfig}
            totals={computeShipmentTotals(shipment)}
            readOnly={readOnly}
            recalculating={recalculatingId === shipment.id}
            onUpdate={handleUpdate}
            onRecalculate={handleRecalculate}
            onRequestDelete={(id) => setDeleteId(id)}
            onOpenAddLegDialog={(id) => setAddLegDialogShipmentId(id)}
            onOpenLoadRouteDialog={(id) => setLoadRouteDialogShipmentId(id)}
            onUpdateLeg={(legId, data) => handleUpdateLeg(shipment.id, legId, data)}
            onDeleteLeg={(legId) => handleDeleteLeg(shipment.id, legId)}
            onAddSurcharge={(legId, data) => handleAddSurcharge(shipment.id, legId, data)}
            onDeleteSurcharge={(legId, surchargeId) =>
              handleDeleteSurcharge(shipment.id, legId, surchargeId)
            }
            allLineItems={allLineItems}
            onAssignLine={handleAssignLine}
            onUnassignLine={handleUnassignLine}
            fmt={fmt}
          />
        ))}
      </div>

      {/* Dialog: Agregar tramo (compartido, scopeado al shipment activo) */}
      <AddFreightLegDialog
        open={!!addLegDialogShipmentId}
        onClose={() => setAddLegDialogShipmentId(null)}
        onAdd={(data) => {
          if (addLegDialogShipmentId) {
            handleAddLegToShipment(addLegDialogShipmentId, data);
          }
        }}
        nextLegOrder={nextLegOrderForShipment}
      />

      {/* Dialog: Cargar desde ruta (compartido, scopeado al shipment activo) */}
      <LoadFreightRouteDialog
        open={!!loadRouteDialogShipmentId}
        onClose={() => setLoadRouteDialogShipmentId(null)}
        onSelect={(route) => {
          if (loadRouteDialogShipmentId) {
            handleLoadFromRoute(loadRouteDialogShipmentId, route);
          }
        }}
      />

      <AlertDialog
        open={!!deleteId}
        onOpenChange={(o) => {
          if (!o) setDeleteId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar envío?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Los ítems asignados quedarán sin envío.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============================================================
// Subcomponente: ShipmentCard
// ============================================================

interface ShipmentCardProps {
  shipment: QuoteShipment;
  incotermConfig: {
    paysInlandOrigin: boolean;
    paysInternational: boolean;
    paysInlandDest: boolean;
    paysHandling: boolean;
    paysInsurance: boolean;
    paysDuties: boolean;
  } | null;
  totals: { totalWeight: number; totalVolume: number; totalFob: number };
  readOnly: boolean;
  recalculating: boolean;
  onUpdate: (
    shipmentId: string,
    patch: Partial<{
      name: string;
      origin: string;
      incoterm: string;
      prorationMethod: ProrationMethod;
      notes: string;
      manualWeight: number | null;
      manualVolume: number | null;
      manualFob: number | null;
    }>
  ) => void;
  onRecalculate: (shipmentId: string) => void;
  onRequestDelete: (shipmentId: string) => void;
  onOpenAddLegDialog: (shipmentId: string) => void;
  onOpenLoadRouteDialog: (shipmentId: string) => void;
  onUpdateLeg: (legId: string, data: Partial<QuoteFreightLeg>) => void;
  onDeleteLeg: (legId: string) => void;
  onAddSurcharge: (
    legId: string,
    data: { concept: string; type: SurchargeType; value: number }
  ) => void;
  onDeleteSurcharge: (legId: string, surchargeId: string) => void;
  allLineItems: Array<{
    id: string;
    materialId: string;
    materialLabel: string;
    quantity: number;
    fob: number;
    shipmentId?: string | null;
  }>;
  onAssignLine: (shipmentId: string, lineItemId: string) => void;
  onUnassignLine: (shipmentId: string, lineItemId: string) => void;
  fmt: (v: number) => string;
}

function ShipmentCard({
  shipment,
  incotermConfig,
  totals,
  readOnly,
  recalculating,
  onUpdate,
  onRecalculate,
  onRequestDelete,
  onOpenAddLegDialog,
  onOpenLoadRouteDialog,
  onUpdateLeg,
  onDeleteLeg,
  onAddSurcharge,
  onDeleteSurcharge,
  allLineItems,
  onAssignLine,
  onUnassignLine,
  fmt,
}: ShipmentCardProps) {
  // Cálculo de legs para este shipment (reusando el hook clásico)
  const calculation = useQuoteFreightCalculation({
    legs: shipment.legs ?? [],
    totalWeight: totals.totalWeight,
    totalVolume: totals.totalVolume,
    totalFob: totals.totalFob,
    incotermConfig,
    provisionRate: 0,
  });

  const calculatedLegs: CalculatedLeg[] = calculation.legs;

  return (
    <Card className="border-info/20">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Ship className="h-4 w-4 text-info" />
            {readOnly ? (
              <span>{shipment.name}</span>
            ) : (
              <Input
                className="h-7 w-48 text-sm"
                defaultValue={shipment.name}
                onBlur={(e) => {
                  const val = e.target.value.trim();
                  if (val && val !== shipment.name) {
                    onUpdate(shipment.id, { name: val });
                  }
                }}
              />
            )}
            {shipment.origin && (
              <Badge variant="outline" className="text-xs">
                {shipment.origin}
              </Badge>
            )}
            {shipment.incoterm && (
              <Badge variant="outline" className="border-info/30 bg-info/10 text-xs text-info">
                {shipment.incoterm}
              </Badge>
            )}
          </CardTitle>

          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {shipment.lineItems?.length ?? 0} ítem(s)
            </span>
            <span className="text-sm font-semibold text-ok tabular-nums">
              {fmt(shipment.totalCost)}
            </span>
            {!readOnly && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  title="Recalcular"
                  onClick={() => onRecalculate(shipment.id)}
                  disabled={recalculating}
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${recalculating ? 'animate-spin' : ''}`} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  title="Eliminar"
                  onClick={() => onRequestDelete(shipment.id)}
                >
                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3 pt-2">
        {/* Datos básicos del envío */}
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <div className="flex items-center gap-1.5">
            <Label className="text-muted-foreground">Origen:</Label>
            {readOnly ? (
              <span>{shipment.origin || '—'}</span>
            ) : (
              <Input
                className="h-6 w-32 text-xs"
                defaultValue={shipment.origin ?? ''}
                placeholder="Ej: Shanghai"
                onBlur={(e) => onUpdate(shipment.id, { origin: e.target.value })}
              />
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <Label className="text-muted-foreground">Incoterm:</Label>
            {readOnly ? (
              <span>{shipment.incoterm || '—'}</span>
            ) : (
              <Select
                value={shipment.incoterm ?? '__none__'}
                onValueChange={(val) =>
                  onUpdate(shipment.id, { incoterm: val === '__none__' ? '' : val })
                }
              >
                <SelectTrigger className="h-6 w-28 text-xs">
                  <SelectValue placeholder="Sin incoterm" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" className="text-xs">
                    Sin incoterm
                  </SelectItem>
                  {[
                    'EXW',
                    'FCA',
                    'FAS',
                    'FOB',
                    'CFR',
                    'CIF',
                    'CPT',
                    'CIP',
                    'DAP',
                    'DPU',
                    'DDP',
                  ].map((code) => (
                    <SelectItem key={code} value={code} className="text-xs">
                      {code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <Label className="text-muted-foreground">Prorrateo:</Label>
            {readOnly ? (
              <span className="font-medium">
                {PRORATION_METHOD_LABELS[shipment.prorationMethod]}
              </span>
            ) : (
              <Select
                value={shipment.prorationMethod}
                onValueChange={(val) =>
                  onUpdate(shipment.id, {
                    prorationMethod: val as ProrationMethod,
                  })
                }
              >
                <SelectTrigger className="h-6 w-36 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRORATION_METHODS.map((m) => (
                    <SelectItem key={m} value={m} className="text-xs">
                      {PRORATION_METHOD_LABELS[m]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <Label className="text-muted-foreground">Tramos:</Label>
            <span className="font-medium">{shipment.legs?.length ?? 0}</span>
          </div>
        </div>

        {/* Overrides manuales de peso/volumen/FOB (si están seteados, reemplazan el derivado) */}
        <ShipmentManualTotalsRow
          shipment={shipment}
          derivedTotals={totals}
          readOnly={readOnly}
          onUpdate={onUpdate}
          fmt={fmt}
        />

        {/* Tramos del envío */}
        <div className="rounded border border-info/10 bg-info/5 p-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-xs font-medium uppercase tracking-wide text-info">Tramos</div>
            {!readOnly && (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1 text-xs"
                  onClick={() => onOpenLoadRouteDialog(shipment.id)}
                  title="Cargar tramos desde una ruta pre-configurada"
                >
                  <ClipboardList className="h-3.5 w-3.5" />
                  Cargar ruta
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1 text-xs"
                  onClick={() => onOpenAddLegDialog(shipment.id)}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Agregar tramo
                </Button>
              </div>
            )}
          </div>

          {calculatedLegs.length === 0 ? (
            <p className="py-2 text-center text-xs text-muted-foreground">
              Sin tramos. Agregá uno o cargá desde una ruta.
            </p>
          ) : (
            <div className="space-y-2">
              {calculatedLegs
                .slice()
                .sort((a, b) => a.legOrder - b.legOrder)
                .map((leg) => (
                  <QuoteFreightLegCard
                    key={leg.id}
                    leg={leg}
                    onUpdate={onUpdateLeg}
                    onDelete={onDeleteLeg}
                    onAddSurcharge={onAddSurcharge}
                    onDeleteSurcharge={onDeleteSurcharge}
                    readOnly={readOnly}
                  />
                ))}
            </div>
          )}
        </div>

        {/* Asignación de productos a este envío */}
        <div className="rounded border border-muted/30 p-2">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Productos en este envío
          </div>
          {allLineItems.length > 0 ? (
            <ul className="space-y-1.5 text-xs">
              {allLineItems.map((li) => {
                const isAssigned =
                  li.shipmentId === shipment.id ||
                  shipment.lineItems?.some((si) => si.id === li.id);
                return (
                  <li key={li.id} className="flex items-center gap-2">
                    {!readOnly && (
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 rounded border-muted accent-indigo-500"
                        checked={!!isAssigned}
                        onChange={() => {
                          if (isAssigned) {
                            onUnassignLine(shipment.id, li.id);
                          } else {
                            onAssignLine(shipment.id, li.id);
                          }
                        }}
                      />
                    )}
                    <span className="truncate flex-1">
                      {li.materialLabel} × {li.quantity}
                    </span>
                    <span className="tabular-nums text-muted-foreground shrink-0">
                      FOB {fmt(li.fob)}
                    </span>
                    {isAssigned &&
                      shipment.lineItems?.find((si) => si.id === li.id)?.proratedFreightCost !=
                        null && (
                        <span className="tabular-nums text-alerta shrink-0">
                          Flete{' '}
                          {fmt(
                            shipment.lineItems!.find((si) => si.id === li.id)!.proratedFreightCost!
                          )}
                        </span>
                      )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground">
              Agregá materiales en la tabla del pricer.
            </p>
          )}
        </div>

        {shipment.notes && (
          <div className="text-xs text-muted-foreground">
            <span className="font-medium">Notas:</span> {shipment.notes}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================
// Subcomponente: ShipmentManualTotalsRow
// Fila de inputs para peso/volumen/FOB del envío con override manual.
// Si el input está vacío (o ≤ 0), se usa el valor derivado de las líneas (auto).
// onBlur dispara el update al backend (que a su vez recalcula totales y prorrateo).
// ============================================================

interface ShipmentManualTotalsRowProps {
  shipment: QuoteShipment;
  derivedTotals: { totalWeight: number; totalVolume: number; totalFob: number };
  readOnly: boolean;
  onUpdate: (
    shipmentId: string,
    patch: Partial<{
      name: string;
      origin: string;
      incoterm: string;
      prorationMethod: ProrationMethod;
      notes: string;
      manualWeight: number | null;
      manualVolume: number | null;
      manualFob: number | null;
    }>
  ) => void;
  fmt: (v: number) => string;
}

function ShipmentManualTotalsRow({
  shipment,
  derivedTotals,
  readOnly,
  onUpdate,
  fmt,
}: ShipmentManualTotalsRowProps) {
  // Estado local controlado por el input para permitir strings vacíos y evitar
  // reseteos visuales hasta confirmar con onBlur.
  const manualWeightStr = shipment.manualWeight != null ? String(shipment.manualWeight) : '';
  const manualVolumeStr = shipment.manualVolume != null ? String(shipment.manualVolume) : '';
  const manualFobStr = shipment.manualFob != null ? String(shipment.manualFob) : '';

  const [weightInput, setWeightInput] = useState<string>(manualWeightStr);
  const [volumeInput, setVolumeInput] = useState<string>(manualVolumeStr);
  const [fobInput, setFobInput] = useState<string>(manualFobStr);

  // Si el shipment se actualiza desde el parent, re-sincronizamos los inputs.
  useEffect(() => {
    setWeightInput(manualWeightStr);
  }, [manualWeightStr]);
  useEffect(() => {
    setVolumeInput(manualVolumeStr);
  }, [manualVolumeStr]);
  useEffect(() => {
    setFobInput(manualFobStr);
  }, [manualFobStr]);

  const commit = (
    field: 'manualWeight' | 'manualVolume' | 'manualFob',
    rawValue: string,
    currentValue: number | null | undefined
  ) => {
    const trimmed = rawValue.trim();
    // Vacío → null (volver a auto)
    if (trimmed === '') {
      if (currentValue != null) {
        onUpdate(shipment.id, { [field]: null } as any);
      }
      return;
    }
    const parsed = Number(trimmed);
    if (!isFinite(parsed) || parsed < 0) return;
    if (currentValue === parsed) return;
    onUpdate(shipment.id, { [field]: parsed } as any);
  };

  const isWeightManual = shipment.manualWeight != null && shipment.manualWeight > 0;
  const isVolumeManual = shipment.manualVolume != null && shipment.manualVolume > 0;
  const isFobManual = shipment.manualFob != null && shipment.manualFob > 0;

  if (readOnly) {
    return (
      <div className="flex flex-wrap items-center gap-4 rounded border border-info/10 bg-info/5 px-3 py-1.5 text-xs">
        <span className="text-muted-foreground">
          Peso:{' '}
          <span className="font-medium text-foreground">
            {isWeightManual
              ? shipment.manualWeight!.toFixed(2)
              : derivedTotals.totalWeight.toFixed(2)}{' '}
            kg
          </span>
          {!isWeightManual && <span className="ml-1 text-[10px] italic opacity-70">(auto)</span>}
        </span>
        <span className="text-muted-foreground">
          Volumen:{' '}
          <span className="font-medium text-foreground">
            {isVolumeManual
              ? shipment.manualVolume!.toFixed(4)
              : derivedTotals.totalVolume.toFixed(4)}{' '}
            m³
          </span>
          {!isVolumeManual && <span className="ml-1 text-[10px] italic opacity-70">(auto)</span>}
        </span>
        <span className="text-muted-foreground">
          FOB:{' '}
          <span className="font-medium text-foreground">
            {fmt(isFobManual ? shipment.manualFob! : derivedTotals.totalFob)}
          </span>
          {!isFobManual && <span className="ml-1 text-[10px] italic opacity-70">(auto)</span>}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-end gap-3 rounded border border-info/10 bg-info/5 px-3 py-2 text-xs">
      <div className="flex flex-col gap-0.5">
        <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
          Peso (kg) {!isWeightManual && <span className="italic opacity-70">(auto)</span>}
        </Label>
        <Input
          type="number"
          step="0.01"
          min="0"
          className="h-7 w-24 text-xs"
          value={weightInput}
          placeholder={derivedTotals.totalWeight.toFixed(2)}
          onChange={(e) => setWeightInput(e.target.value)}
          onBlur={() => commit('manualWeight', weightInput, shipment.manualWeight)}
        />
      </div>
      <div className="flex flex-col gap-0.5">
        <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
          Volumen (m³) {!isVolumeManual && <span className="italic opacity-70">(auto)</span>}
        </Label>
        <Input
          type="number"
          step="0.0001"
          min="0"
          className="h-7 w-24 text-xs"
          value={volumeInput}
          placeholder={derivedTotals.totalVolume.toFixed(4)}
          onChange={(e) => setVolumeInput(e.target.value)}
          onBlur={() => commit('manualVolume', volumeInput, shipment.manualVolume)}
        />
      </div>
      <div className="flex flex-col gap-0.5">
        <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
          FOB {!isFobManual && <span className="italic opacity-70">(auto)</span>}
        </Label>
        <Input
          type="number"
          step="0.01"
          min="0"
          className="h-7 w-28 text-xs"
          value={fobInput}
          placeholder={derivedTotals.totalFob.toFixed(2)}
          onChange={(e) => setFobInput(e.target.value)}
          onBlur={() => commit('manualFob', fobInput, shipment.manualFob)}
        />
      </div>
      <div className="ml-auto text-[10px] italic text-muted-foreground">
        Vacío = usa total derivado de líneas
      </div>
    </div>
  );
}

export default QuoteLogisticsSection;
